/**
 * CaseStudyPage — /case-studies/$slug individual case study.
 *
 * Renders build-time HTML from the manifest. No runtime markdown parsing.
 * Layout: header with de-identified client meta and stack, an outcome
 * callout (headline metric + real quote when present), the prose body with
 * a sticky at-a-glance / table-of-contents rail on desktop, a visible FAQ
 * accordion (matching the FAQPage JSON-LD), prev/next navigation, and a
 * closing CTA to /intake.
 */
import { Helmet } from 'react-helmet-async';
import { Link, useParams } from '@tanstack/react-router';
import SchemaJsonLd from '../components/primitives/SchemaJsonLd.jsx';
import Reveal from '../components/ui/Reveal.jsx';
import Tag from '../components/ui/Tag.jsx';
import Button from '../components/ui/Button.jsx';
import FAQItem from '../components/primitives/FAQItem.jsx';
import { Metric } from '../components/primitives/MetricCard.jsx';
import { readingTimeMins } from '../components/primitives/CaseStudyCard.jsx';
import { caseStudies } from '../content/case-studies-manifest.js';
import { caseStudiesIndex } from '../content/caseStudies.js';

function formatDate(dateStr) {
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr || '';
  return d.toLocaleDateString('en-AU', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

function CaseStudyNotFound({ slug }) {
  return (
    <>
      <Helmet>
        <title>Case study not found — Case Studies</title>
      </Helmet>
      <section className="section-pad bg-surface text-ink-50">
        <div className="container-page max-w-prose">
          <h1 className="text-display-lg font-display font-semibold mb-4">Case study not found</h1>
          <p className="text-body text-ink-200 mb-8">
            Could not find case study: <code>"{slug}"</code>
          </p>
          <Link
            to="/case-studies"
            className="text-body text-brand-blue-bright hover:text-brand-blue-pale transition-colors"
          >
            {caseStudiesIndex.backLinkLabel}
          </Link>
        </div>
      </section>
    </>
  );
}

function FactRow({ label, children }) {
  if (!children) return null;
  return (
    <div className="flex items-baseline justify-between gap-4 py-2.5 border-t border-white/[.06] first:border-t-0">
      <dt className="font-mono text-mono-xs tracking-[0.12em] uppercase text-ink-300 whitespace-nowrap">
        {label}
      </dt>
      <dd className="text-body-sm text-ink-100 text-right m-0">{children}</dd>
    </div>
  );
}

/** Sticky desktop rail: at-a-glance facts, stack, and on-this-page TOC. */
function StudyRail({ entry, tocHeadings }) {
  const labels = caseStudiesIndex.factLabels;
  return (
    <aside className="hidden lg:block sticky top-28">
      <div className="rounded-lg border border-white/[.08] bg-white/[.02] p-6">
        <h2 className="font-mono text-mono-sm tracking-[0.14em] uppercase text-ink-300 font-normal m-0">
          {caseStudiesIndex.factsTitle}
        </h2>
        <dl className="mt-4 m-0">
          <FactRow label={labels.client}>{entry.client}</FactRow>
          <FactRow label={labels.type}>
            {caseStudiesIndex.typeLabels[entry.type] ?? entry.type}
          </FactRow>
          <FactRow label={labels.date}>{formatDate(entry.date)}</FactRow>
          <FactRow label={labels.readTime}>{readingTimeMins(entry.wordCount)} min</FactRow>
        </dl>

        {Array.isArray(entry.stack) && entry.stack.length > 0 && (
          <div className="mt-5 pt-4 border-t border-white/[.06]">
            <span className="font-mono text-mono-xs tracking-[0.12em] uppercase text-ink-300">
              {labels.stack}
            </span>
            <div className="mt-3 flex flex-wrap gap-2">
              {entry.stack.map((s) => (
                <Tag key={s}>{s}</Tag>
              ))}
            </div>
          </div>
        )}
      </div>

      {tocHeadings.length > 1 && (
        <nav
          aria-label={caseStudiesIndex.onThisPage}
          className="mt-6 rounded-lg border border-white/[.08] bg-white/[.02] p-6"
        >
          <span className="font-mono text-mono-sm tracking-[0.14em] uppercase text-ink-300">
            {caseStudiesIndex.onThisPage}
          </span>
          <ul className="mt-4 m-0 p-0 list-none flex flex-col gap-2.5">
            {tocHeadings.map((h) => (
              <li key={h.slug}>
                <a
                  href={`#${h.slug}`}
                  className="text-body-sm text-ink-200 hover:text-brand-blue-bright transition-colors"
                >
                  {h.text}
                </a>
              </li>
            ))}
          </ul>
        </nav>
      )}
    </aside>
  );
}

/** Outcome callout: headline metric, one-line outcome, real quote when present. */
function OutcomeCallout({ entry }) {
  if (!entry.outcome && !entry.metric && !entry.quote) return null;
  return (
    <div className="rounded-lg border border-white/[.08] bg-white/[.02] p-6 md:p-8 mb-10">
      <div className={entry.quote ? 'grid md:grid-cols-2 gap-8 md:gap-12 md:items-center' : ''}>
        <div>
          {entry.metric && (
            <div className="mb-5">
              <Metric value={entry.metric} unit={entry.metricUnit} label={entry.metricLabel} />
            </div>
          )}
          {entry.outcome && (
            <>
              <span className="font-mono text-mono-xs tracking-[0.12em] uppercase text-ink-300">
                {caseStudiesIndex.outcomeLabel}
              </span>
              <p className="mt-1.5 text-body-lg text-ink-100 m-0">{entry.outcome}</p>
            </>
          )}
        </div>

        {entry.quote && (
          <blockquote className="m-0 md:border-l md:border-white/[.1] md:pl-12">
            <p className="text-display-sm font-display font-medium text-ink-50 leading-snug m-0">
              “{entry.quote}”
            </p>
            {entry.quoteAttrib && (
              <cite className="mt-4 block font-mono text-mono-xs uppercase tracking-[0.12em] text-ink-300 not-italic">
                {entry.quoteAttrib}
              </cite>
            )}
          </blockquote>
        )}
      </div>
    </div>
  );
}

function PrevNextCard({ entry, directionLabel }) {
  if (!entry) return null;
  return (
    <Link
      to="/case-studies/$slug"
      params={{ slug: entry.slug }}
      className={
        'group flex flex-col rounded-lg border border-white/[.08] bg-white/[.02] p-5 ' +
        'transition-colors duration-200 hover:bg-white/[.04] hover:border-white/[.14]'
      }
    >
      <span className="font-mono text-mono-xs tracking-[0.12em] uppercase text-ink-300">
        {directionLabel}
      </span>
      <span className="mt-2 text-body font-display font-semibold text-ink-50 group-hover:text-brand-blue-bright transition-colors">
        {entry.title}
      </span>
    </Link>
  );
}

export default function CaseStudyPage() {
  // strict: false lets useParams read $slug from any matched route without type-narrowing to a single route ID.
  const { slug } = useParams({ strict: false });
  const idx = caseStudies.findIndex((c) => c.slug === slug);
  const entry = idx === -1 ? undefined : caseStudies[idx];

  if (!entry) {
    if (typeof window === 'undefined') {
      console.warn(`[ssr] CaseStudyPage: no case study found for slug "${slug}"`);
    }
    return <CaseStudyNotFound slug={slug} />;
  }

  const newer = idx > 0 ? caseStudies[idx - 1] : null;
  const older = idx < caseStudies.length - 1 ? caseStudies[idx + 1] : null;

  const entryUrl = entry.canonical || `https://artificersai.com/case-studies/${entry.slug}`;
  const metaParts = [
    entry.client,
    formatDate(entry.date),
    `${readingTimeMins(entry.wordCount)} min read`,
  ].filter(Boolean);
  const hasFaqs = Array.isArray(entry.faqs) && entry.faqs.length > 0;
  const tocHeadings = (entry.headings || []).filter((h) => h.depth === 2);

  const mainEntitySchema = {
    '@context': 'https://schema.org',
    '@type': entry.type === 'tool' ? 'CreativeWork' : 'Article',
    headline: entry.title,
    name: entry.title,
    description: entry.description,
    datePublished: entry.date,
    dateModified: entry.dateModified,
    wordCount: entry.wordCount,
    keywords: entry.keywords || [],
    inLanguage: 'en-AU',
    author: {
      '@type': 'Person',
      name: entry.author,
      url: 'https://artificersai.com/about',
    },
    publisher: {
      '@type': 'Organization',
      name: "The Artificer's AI",
      url: 'https://artificersai.com',
    },
    url: entryUrl,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': entryUrl,
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://artificersai.com/' },
      { '@type': 'ListItem', position: 2, name: 'Case Studies', item: 'https://artificersai.com/case-studies' },
      { '@type': 'ListItem', position: 3, name: entry.title, item: entryUrl },
    ],
  };

  const faqSchema = hasFaqs
    ? {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: entry.faqs.map((f) => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: {
            '@type': 'Answer',
            text: f.a,
          },
        })),
      }
    : null;

  return (
    <>
      <Helmet>
        <title>{entry.title} — Case Studies</title>
        <meta name="description" content={entry.description} />
        <link rel="canonical" href={entryUrl} />
        <meta property="og:title" content={`${entry.title} — Case Studies`} />
        <meta property="og:description" content={entry.description} />
        <meta property="og:url" content={entryUrl} />
      </Helmet>

      <SchemaJsonLd data={mainEntitySchema} />
      <SchemaJsonLd data={breadcrumbSchema} />
      {faqSchema && <SchemaJsonLd data={faqSchema} />}

      <section className="section-pad bg-surface text-ink-50">
        <div className="container-page">
          <Reveal as="header" delay={0} className="mb-10 max-w-[820px]">
            <Link
              to="/case-studies"
              className="font-mono text-mono-md text-ink-300 hover:text-brand-blue-bright transition-colors"
            >
              {caseStudiesIndex.backLinkLabel}
            </Link>

            <div className="mt-6 flex items-center gap-3">
              <span className="font-mono text-mono-xs tracking-[0.12em] uppercase text-ink-300">
                {entry.tag || entry.type}
              </span>
            </div>

            <h1 className="mt-3 text-display-lg font-display font-semibold">{entry.title}</h1>

            <p className="mt-4 text-body-lg text-ink-200 m-0">{entry.description}</p>

            {metaParts.length > 0 && (
              <p className="mt-5 font-mono text-mono-xs tracking-[0.08em] uppercase text-ink-300 m-0">
                {metaParts.join(' · ')}
              </p>
            )}

            {Array.isArray(entry.stack) && entry.stack.length > 0 && (
              <div className="mt-5 flex flex-wrap gap-2 lg:hidden">
                {entry.stack.map((s) => (
                  <Tag key={s}>{s}</Tag>
                ))}
              </div>
            )}
          </Reveal>

          <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_300px] lg:gap-16 lg:items-start">
            <div className="max-w-[820px]">
              <OutcomeCallout entry={entry} />

              {/* entry.html is generated at build time from trusted markdown; no user input enters this path. */}
              <div
                className="post-body"
                dangerouslySetInnerHTML={{ __html: entry.html }}
              />

              {hasFaqs && (
                <section aria-labelledby="case-study-faqs" className="mt-14">
                  <h2
                    id="case-study-faqs"
                    className="text-h2 font-display font-semibold text-ink-50 mb-6"
                  >
                    {caseStudiesIndex.faqHeading}
                  </h2>
                  <div>
                    {entry.faqs.map((f) => (
                      <FAQItem key={f.q} q={f.q} a={f.a} />
                    ))}
                  </div>
                </section>
              )}
            </div>

            <StudyRail entry={entry} tocHeadings={tocHeadings} />
          </div>

          {(newer || older) && (
            <Reveal as="nav" delay={0} className="mt-16 pt-10 border-t border-white/[.08]">
              <span className="font-mono text-mono-sm tracking-[0.14em] uppercase text-ink-300">
                {caseStudiesIndex.prevNext.heading}
              </span>
              <div className="mt-4 grid sm:grid-cols-2 gap-4">
                <PrevNextCard entry={newer} directionLabel={caseStudiesIndex.prevNext.newer} />
                <PrevNextCard entry={older} directionLabel={caseStudiesIndex.prevNext.older} />
              </div>
            </Reveal>
          )}

          <Reveal as="div" delay={0} className="mt-16 pt-10 border-t border-white/[.08]">
            <div className="max-w-[560px]">
              <h2 className="text-display-md font-display font-semibold text-ink-50 m-0">
                {caseStudiesIndex.cta.heading}
              </h2>
              <p className="mt-3 text-body-lg text-ink-200 m-0">{caseStudiesIndex.cta.body}</p>
              <div className="mt-6">
                <Button as={Link} to="/intake">
                  {caseStudiesIndex.cta.button}
                </Button>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
